import { FunctionComponent, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiAccountPlus,
  mdiMapMarkerPlus,
  mdiAccount,
  mdiAlertCircleOutline,
} from "@mdi/js";
import ModalDialog from "../../../../ui/new_components/common/modal";
import { CustomerSpocModel } from "../../../../context_providers/auth/user_model";

interface Props {
  spocs: CustomerSpocModel[];
  onChange: (spocs: CustomerSpocModel[]) => void;
  selectedSpocId?: string;
  onSelectSpoc?: (id: string) => void;
  onAddAddress?: (id: string) => void;
  onDeleteSpoc?: (spoc: CustomerSpocModel) => void;
  viewOnly?: boolean;
  id_prefix?: string;
}

const emptySpoc = (): CustomerSpocModel => ({
  id: `spoc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  email: "",
  website: "",
  name_of_contact_person: "",
  designation_of_contact_person: "",
  mobile_no_of_contact_person: "",
  email_id_of_contact_person: "",
  name_of_accounts_spoc: "",
  designation_of_accounts_spoc: "",
  mobile_no_of_accounts_spoc: "",
  email_id_of_accounts_spoc: "",
  delivery_addresses: [],
});

const CustomerSpocSection: FunctionComponent<Props> = ({
  spocs,
  onChange,
  onSelectSpoc,
  onAddAddress,
  onDeleteSpoc,
  viewOnly = false,
  id_prefix = "customer-spoc",
}) => {
  const [editor, setEditor] = useState<{
    index: number | null;
    draft: CustomerSpocModel;
  } | null>(null);
  const [error, setError] = useState("");
  const [spocToDelete, setSpocToDelete] = useState<CustomerSpocModel | null>(
    null,
  );

  const setDraft = (patch: Partial<CustomerSpocModel>) =>
    setEditor((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  const saveContact = () => {
    if (!editor) return;
    const d = editor.draft;
    if (
      !d.name_of_contact_person?.trim() ||
      !d.mobile_no_of_contact_person?.trim()
    ) {
      setError("Name and Contact No are required.");
      return;
    }
    const nameKey = d.name_of_contact_person.trim().toLowerCase();
    const mobileKey = d.mobile_no_of_contact_person.trim();
    const duplicate = spocs.some(
      (s, i) =>
        i !== editor.index &&
        (s.name_of_contact_person || "").trim().toLowerCase() === nameKey &&
        (s.mobile_no_of_contact_person || "").trim() === mobileKey,
    );
    if (duplicate) {
      setError(
        "A contact with the same name and contact number already exists.",
      );
      return;
    }
    setError("");
    if (editor.index === null) {
      onChange([...spocs, d]);
      onSelectSpoc?.(d.id);
    } else {
      onChange(spocs.map((s, i) => (i === editor.index ? d : s)));
    }
    setEditor(null);
  };

  const field = (label: string, value?: string) => (
    <div>
      <span className="text-gray-400">{label}: </span>
      <span className="text-gray-700">{value || "--"}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {spocs.length === 0 && (
        <p className="text-sm text-gray-400">
          No contact added yet. Add a contact, then add its delivery addresses.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {spocs.map((spoc, si) => {
          return (
            <div
              key={spoc.id || si}
              id={`${id_prefix}-card-${si}`}
              className="rounded-lg border p-3 bg-gray-50"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon
                    path={mdiAccount}
                    size={0.8}
                    className="text-myPrimaryColor"
                  />
                  <div>
                    <p className="font-semibold text-sm">
                      {spoc.name_of_contact_person || `Contact ${si + 1}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {spoc.designation_of_contact_person || ""}
                    </p>
                  </div>
                </div>
                {!viewOnly && (
                  <div className="flex items-center gap-3">
                    <button
                      id={`${id_prefix}-card-${si}-edit-btn`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditor({ index: si, draft: { ...spoc } });
                      }}
                      className="text-myPrimaryColor text-xs font-semibold underline"
                      title="Edit contact"
                    >
                      Edit
                    </button>
                    <button
                      id={`${id_prefix}-card-${si}-delete-btn`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSpocToDelete(spoc);
                      }}
                      className="text-red-500 text-xs font-semibold underline"
                      title="Delete contact"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <div className="text-xs grid grid-cols-1 gap-0.5">
                {field("Contact No", spoc.mobile_no_of_contact_person)}
                {field(
                  "Email",
                  spoc.email_id_of_contact_person || spoc.email,
                )}
              </div>

              <div className="mt-2 flex items-center justify-end">
                {!viewOnly &&
                  (spoc.delivery_addresses?.length || 0) === 0 && (
                    <button
                      id={`${id_prefix}-card-${si}-add-address-btn`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectSpoc?.(spoc.id);
                        onAddAddress?.(spoc.id);
                      }}
                      className="text-xs text-white bg-myPrimaryColor px-2 py-0.5 rounded flex items-center gap-1"
                    >
                      <Icon path={mdiMapMarkerPlus} size={0.6} /> Add Delivery
                      Address
                    </button>
                  )}
              </div>

              {viewOnly && (spoc.delivery_addresses || []).length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {spoc.delivery_addresses.map((addr, ai) => (
                    <div
                      key={ai}
                      className="border rounded-md bg-white p-2 text-xs"
                    >
                      <div className="font-semibold">
                        {addr.branch_name || "—"}
                      </div>
                      <div className="text-gray-500">
                        {[
                          addr.line1,
                          addr.line2,
                          addr.line3,
                          addr.city,
                          addr.state,
                          addr.country,
                          addr.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!viewOnly && (
        <div>
          <button
            id={`${id_prefix}-add-contact-btn`}
            type="button"
            onClick={() => setEditor({ index: null, draft: emptySpoc() })}
            className="cursor-pointer text-white bg-myPrimaryColor px-3 py-1 rounded font-semibold flex items-center gap-1"
          >
            <Icon path={mdiAccountPlus} size={0.8} /> Add Contact
          </button>
        </div>
      )}

      {editor && (
        <ModalDialog
          id={`${id_prefix}-editor-modal`}
          title={editor.index === null ? "Add Contact (SPOC)" : "Edit Contact (SPOC)"}
          show={!!editor}
          onClose={() => {
            setEditor(null);
            setError("");
          }}
        >
          <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              ["Name of Contact Person *", "name_of_contact_person"],
              ["Contact No *", "mobile_no_of_contact_person"],
              ["Email ID of Contact Person", "email_id_of_contact_person"],
              ["Email ID", "email"],
              ["Designation", "designation_of_contact_person"],
              // Accounts SPOC fields hidden for now.
              // ["Name of Account SPOC", "name_of_accounts_spoc"],
              // ["Designation of Accounts SPOC", "designation_of_accounts_spoc"],
              // ["Email ID of Account SPOC", "email_id_of_accounts_spoc"],
              // ["Mobile No. of Accounts SPOC", "mobile_no_of_accounts_spoc"],
              ["Website", "website"],
            ].map(([label, key]) => {
              const fieldId = `${id_prefix}-${key.replace(/_/g, "-")}`;
              return (
                <div className="grid gap-1" key={key}>
                  <label className="text-sm" htmlFor={fieldId}>
                    {label}
                  </label>
                  <input
                    id={fieldId}
                    className="border rounded-md px-3 py-1"
                    value={(editor.draft as any)[key] ?? ""}
                    onChange={(e) => setDraft({ [key]: e.target.value })}
                  />
                </div>
              );
            })}
            {error && (
              <p
                id={`${id_prefix}-editor-error`}
                className="md:col-span-3 text-xs text-red-500"
              >
                {error}
              </p>
            )}
            <div className="md:col-span-3 flex justify-end gap-2">
              <button
                id={`${id_prefix}-editor-cancel-btn`}
                type="button"
                onClick={() => {
                  setEditor(null);
                  setError("");
                }}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                id={`${id_prefix}-editor-save-btn`}
                type="button"
                onClick={saveContact}
                className="px-3 py-1 bg-myPrimaryColor text-white rounded text-sm"
              >
                Save Contact
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {spocToDelete && (
        <ModalDialog
          id={`${id_prefix}-delete-modal`}
          title="Delete Contact (POC)"
          show={!!spocToDelete}
          onClose={() => setSpocToDelete(null)}
        >
          <div className="p-4 flex flex-col gap-4">
            <div className="flex gap-3">
              <Icon
                path={mdiAlertCircleOutline}
                size={1.3}
                className="text-red-500 shrink-0"
              />
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">
                  Are you sure you want to delete this contact?
                </p>
                <p>
                  Deleting{" "}
                  <span className="font-semibold">
                    {spocToDelete.name_of_contact_person || "this contact"}
                  </span>{" "}
                  will also permanently delete{" "}
                  {(spocToDelete.delivery_addresses?.length || 0) === 1
                    ? "its linked delivery address"
                    : `its ${
                        spocToDelete.delivery_addresses?.length || 0
                      } linked delivery addresses`}
                  . This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                id={`${id_prefix}-delete-cancel-btn`}
                type="button"
                onClick={() => setSpocToDelete(null)}
                className="px-3 py-1 border rounded text-sm"
              >
                Cancel
              </button>
              <button
                id={`${id_prefix}-delete-confirm-btn`}
                type="button"
                onClick={() => {
                  onDeleteSpoc?.(spocToDelete);
                  setSpocToDelete(null);
                }}
                className="px-3 py-1 bg-red-500 text-white rounded text-sm font-semibold"
              >
                Delete Contact
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
    </div>
  );
};

export default CustomerSpocSection;
